'use client';

import {
  Box,
  Chip,
  Divider,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import type {
  InventoryResource,
  SecurityScan,
  SecurityScansSummaryResponse,
} from '@repo/api-gw-sdk';
import { SecurityScanResultTypeEnum } from '@repo/api-gw-sdk';

import { useDAL } from '@/contexts/useDAL';
import { useBackupVaults } from '@/data/vaults/useBackupVaults';
import { isDateValid } from '@/utils/dateTime';
import { dayjs, DATE_FORMAT } from '@/utils/dayjs';

import { AnomalyChart } from './anomalyChart';
import { getEvidenceStrings } from './securityUtils';

import { StatusCardHeader } from '../panels/instance/tabs/overview-tab/statusCardHeader';
import { StatusCardWrapper } from '../panels/instance/tabs/overview-tab/statusCardWrapper';
import { SnapshotIndicator } from '../snapshots/SnapshotIndicator';

const TYPE_ICON: Partial<Record<SecurityScanResultTypeEnum, string>> = {
  [SecurityScanResultTypeEnum.File]: 'material-symbols-draft-outline-rounded',
  [SecurityScanResultTypeEnum.Directory]: 'material-symbols-folder',
  [SecurityScanResultTypeEnum.Database]: 'material-symbols-database-outline',
  [SecurityScanResultTypeEnum.Schema]: 'material-symbols-account-tree-outline',
  [SecurityScanResultTypeEnum.Table]: 'material-symbols-table',
  [SecurityScanResultTypeEnum.Column]: 'material-symbols-table-chart-outline',
  [SecurityScanResultTypeEnum.Row]: 'material-symbols-table',
};

interface DataAnomalyCardProps {
  resource: InventoryResource;
  scans: SecurityScansSummaryResponse;
}

export const DataAnomalyCard = ({ resource, scans }: DataAnomalyCardProps) => {
  const lastScan = scans?.lastScan;
  const lastClean = scans?.lastClean;
  const results = lastScan?.results ?? [];

  const resultScores = results
    .map((r) => r.score)
    .filter((s): s is number => s !== null);
  const confidenceScore =
    lastScan?.confidenceScore ??
    (resultScores.length > 0
      ? resultScores.reduce((a, b) => a + b, 0) / resultScores.length
      : null);

  const infectedCount = scans?.infectedSnapshotsCount ?? 0;
  const hasLastClean = !!lastClean && isDateValid(lastClean.createdTime);

  return (
    <StatusCardWrapper borderColor='var(--mui-palette-warning-main)'>
      <Stack gap='24px'>
        <Stack marginBottom='12px' alignItems='center' spacing='12px'>
          <Stack
            direction='row'
            width='100%'
            padding='0 24px 0 40px'
            alignItems='center'
          >
            <StatusCardHeader
              title='Data Anomaly'
              actions={[]}
              entity={resource}
            />
            <Box sx={{ flex: 1 }} />
            {confidenceScore !== null && (
              <Tooltip title='Score' placement='top'>
                <Chip
                  label={`${Math.round(confidenceScore * 100)}%`}
                  size='small'
                  sx={{
                    backgroundColor: 'var(--mui-palette-warning-main)',
                    color: 'var(--mui-palette-warning-contrastText)',
                    fontWeight: 600,
                    fontSize: '11px',
                    cursor: 'default',
                  }}
                />
              </Tooltip>
            )}
          </Stack>
          <Typography className='font-semibold' fontSize='16px'>
            Data Anomaly Detected
          </Typography>
        </Stack>

        {/* Description row */}
        <Stack marginX='40px' gap='4px'>
          <Typography variant='subtitle1' lineHeight='20px'>
            {`${infectedCount} ${infectedCount === 1 ? 'snapshot has' : 'snapshots have'} anomaly indicators.`}
          </Typography>

          {/* Justification + last clean snapshot — each 50% */}
          <Stack direction='row' gap='40px' alignItems='flex-start'>
            <Box flex={60}>
              {lastScan?.justification && (
                <Typography
                  variant='subtitle1'
                  color='text.secondary'
                  lineHeight='20px'
                >
                  {lastScan.justification}
                </Typography>
              )}
            </Box>
            <Divider orientation='vertical' flexItem />
            <Stack flex={40} justifyContent='flex-start'>
              {hasLastClean ? (
                <CleanSnapshotRow scan={lastClean} />
              ) : (
                <Stack
                  direction='row'
                  alignItems='center'
                  justifyContent='space-between'
                >
                  <Typography flexGrow={1}>Latest clean</Typography>
                  <Typography>None</Typography>
                </Stack>
              )}
            </Stack>
          </Stack>
        </Stack>

        <Divider sx={{ margin: '0 40px', borderStyle: 'dashed' }} />

        <Typography paddingX='40px' variant='subtitle1'>
          {`Anomaly indicators from the latest snapshot on ${dayjs.utc(lastScan?.createdTime).format(DATE_FORMAT)}:`}
        </Typography>

        {/* Evidence list — one row per result, no grouping */}
        <Stack marginY='12px' divider={<Divider sx={{ marginX: '40px' }} />}>
          {results.map((result, idx) => {
            const evidenceStrings = getEvidenceStrings(result.evidenceType);
            const icon = result.type ? TYPE_ICON[result.type] : undefined;

            return (
              <Stack
                key={`${result.evidenceType}-${result.path}-${idx}`}
                direction='row'
                gap='16px'
                paddingX='40px'
                paddingY='16px'
                alignItems='flex-start'
              >
                <Stack gap='4px' minWidth='160px' maxWidth='200px'>
                  <Typography variant='h4'>{evidenceStrings.label}</Typography>
                  {result.path && (
                    <Stack direction='row' alignItems='center' gap='4px'>
                      {icon && (
                        <i
                          className={icon}
                          style={{
                            fontSize: '14px',
                            color: 'var(--mui-palette-text-secondary)',
                            flexShrink: 0,
                          }}
                        />
                      )}
                      <Typography
                        variant='subtitle2'
                        color='text.secondary'
                        sx={{ fontFamily: 'monospace' }}
                      >
                        {result.path}
                      </Typography>
                    </Stack>
                  )}
                </Stack>

                {result.anomalyGraph ? (
                  <Box flex={1} minWidth={0}>
                    <AnomalyChart graphData={result.anomalyGraph} />
                  </Box>
                ) : (
                  <Box flex={1} />
                )}
              </Stack>
            );
          })}
        </Stack>
      </Stack>
    </StatusCardWrapper>
  );
};

const CleanSnapshotRow = ({ scan }: { scan: SecurityScan }) => {
  const dal = useDAL();
  const { vaults } = useBackupVaults();
  const { data: snapshot, isLoading } = dal.inventory.snapshots.useSnapshot(
    scan.snapshotId
  );
  const vault = vaults.find((v) => v.id === snapshot?.snapshot.vaultId);

  if (isLoading) {
    return <Skeleton />;
  }

  return (
    <Stack
      gap='12px'
      direction='row'
      alignItems='center'
      justifyContent='space-between'
    >
      <Typography width='120px'>Latest clean</Typography>
      <Stack direction='row' gap='8px' alignItems='center'>
        <SnapshotIndicator
          sx={{ margin: '4px' }}
          backgroundColor={vault?.backgroundColor || null}
        />
        <Typography noWrap>
          {dayjs.utc(scan.createdTime).format(DATE_FORMAT)}
        </Typography>
      </Stack>
    </Stack>
  );
};
